import { ActivityIngredient } from '@apps/main/modules/recipe/ingredients';

import type {
  IngredientId,
  SelectedTicketInfo,
} from '@libs/trip-planner-interfaces';
import { assertValue, parseStringToNumber } from '@libs/utils';

import {
  ACTIVITY_PROPS_SEPARATOR,
  SELECTED_TICKET_PREFIX,
  TICKET_PROPS_SEPARATOR,
  TICKET_PROPS_WRAPPER_CLOSE,
  TICKET_PROPS_WRAPPER_OPEN,
} from './constants';

export function deserializeActivity(
  value: string,
  parentIngredientId: IngredientId
): ActivityIngredient {
  const { code, offset, serializedTickets } =
    deserializeIngredientParameters(value);

  const { tickets, selectedTicketDate } = deserializeTickets(serializedTickets);

  const subIngredient = new ActivityIngredient(code, parentIngredientId);
  subIngredient.offset = selectedTicketDate !== undefined ? undefined : offset;
  subIngredient.tickets = tickets;
  subIngredient.selectedTicketDate = selectedTicketDate;

  return subIngredient;
}

function deserializeIngredientParameters(value: string): {
  code: string;
  offset: number;
  serializedTickets: string[];
} {
  const serializedActivityIngredient = value.replace(
    `${SELECTED_TICKET_PREFIX}`,
    ''
  );

  const [code, serializedOffset, ...serializedTickets] =
    serializedActivityIngredient.split(
      new RegExp(
        `${ACTIVITY_PROPS_SEPARATOR}(?![^${TICKET_PROPS_WRAPPER_OPEN}${TICKET_PROPS_WRAPPER_CLOSE}]*\\${TICKET_PROPS_WRAPPER_CLOSE})`
      )
    );

  assertValue(
    code,
    'activitySerializer.ts > ActivitySerializer > deserialize > code'
  );

  assertValue(
    serializedOffset,
    'activitySerializer.ts > ActivitySerializer > deserialize > serializedOffset'
  );

  const offset = parseStringToNumber(serializedOffset) ?? 0;

  return {
    code,
    offset,
    serializedTickets,
  };
}

function deserializeTickets(serializedTickets: string[]): {
  tickets: SelectedTicketInfo[];
  selectedTicketDate?: string;
} {
  let selectedTicketDate: string | undefined;

  const tickets = serializedTickets
    .filter((serializedTicket) => serializedTicket !== '')
    .map<SelectedTicketInfo>((serializedTicket) => {
      const [
        rateId,
        ageRangeId,
        quantity,
        timeSlotId,
        getQuantityFromPax,
        ticketSelectedDate,
      ] = serializedTicket.slice(1, -1).split(TICKET_PROPS_SEPARATOR);

      assertValue(
        rateId,
        'activitySerializer.ts > ActivitySerializer > deserialize > rateId'
      );
      assertValue(
        ageRangeId,
        'activitySerializer.ts > ActivitySerializer > deserialize > ageRangeId'
      );
      assertValue(
        quantity,
        'activitySerializer.ts > ActivitySerializer > deserialize > quantity'
      );
      assertValue(
        timeSlotId,
        'activitySerializer.ts > ActivitySerializer > deserialize > timeSlotId'
      );

      if (ticketSelectedDate && !selectedTicketDate) {
        selectedTicketDate = ticketSelectedDate;
      }

      return {
        ageRangeId: ageRangeId === '' ? undefined : ageRangeId,
        quantity: getQuantityFromPax === '1' ? undefined : Number(quantity),
        rateId: rateId === '' ? undefined : rateId,
        timeSlotId: timeSlotId.length
          ? decodeURIComponent(timeSlotId)
          : undefined,
        getQuantityFromPax: getQuantityFromPax === '1',
      };
    });

  return { tickets, selectedTicketDate };
}
